/* ═══════════════════════════════════════════════════════
   NYC Suite: sign a shared device out after a while without use
   ═══════════════════════════════════════════════════════
   docs/ops/password-signin.md, "Idle sign-out". A setting of THIS device (the
   browser's own storage), Off unless an admin turns it on from the home page.
   When on, the Suite warns a minute before and then signs out after that
   many minutes with no tap, click, key or scroll in any tab of the device
   (the last use is shared through the browser's storage). The
   second-display window (/display), which nobody touches by design, runs it
   too, so it counts the console's use and goes out only when nothing on the
   device has been used.
   Pure logic here; src/components/IdleSignOut.jsx wires it to the page.
*/

export const IDLE_STORAGE_KEY = "nycsuite.idleSignOutMinutes";
// The last use in ANY tab of this device (ms), so a tab left on the home page
// does not sign the device out while the rig console is in use in another.
export const LAST_USE_KEY = "nycsuite.lastUse";
// A tab writes its use at most this often.
export const SHARE_EVERY_MS = 5_000;
// 0 is Off, the default.
export const IDLE_CHOICES = Object.freeze([0, 5, 10, 15, 30, 60]);
export const WARN_SECONDS = 60;

export function readIdleMinutes(storage) {
  try {
    const n = Number(storage?.getItem(IDLE_STORAGE_KEY));
    return IDLE_CHOICES.includes(n) ? n : 0;
  } catch {
    return 0; // storage blocked (private mode): Off
  }
}

export function writeIdleMinutes(storage, minutes) {
  const n = IDLE_CHOICES.includes(Number(minutes)) ? Number(minutes) : 0;
  try {
    if (n === 0) storage?.removeItem(IDLE_STORAGE_KEY);
    else storage?.setItem(IDLE_STORAGE_KEY, String(n));
  } catch {
    /* storage blocked: the setting simply does not stick */
  }
  return n;
}

/**
 * Where the countdown stands, from the last use and now (both ms).
 *   { state: "off" }                    the setting is Off;
 *   { state: "active", msLeft }         in use; msLeft until sign-out;
 *   { state: "warning", secondsLeft }   the last minute;
 *   { state: "expired" }                sign out now.
 */
export function idleState(minutes, lastActivity, now) {
  if (!minutes) return { state: "off" };
  const msLeft = minutes * 60_000 - (now - lastActivity);
  if (msLeft <= 0) return { state: "expired" };
  if (msLeft <= WARN_SECONDS * 1000) return { state: "warning", secondsLeft: Math.ceil(msLeft / 1000) };
  return { state: "active", msLeft };
}

export function readLastUse(storage) {
  try {
    const n = Number(storage?.getItem(LAST_USE_KEY));
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}

export function writeLastUse(storage, at) {
  try {
    storage?.setItem(LAST_USE_KEY, String(at));
  } catch {
    /* storage blocked: this tab counts on its own */
  }
}

// The later of this tab's last use and the device's, never in the future.
export function lastUseOf(ownLast, sharedLast, now) {
  const shared = sharedLast > 0 && sharedLast <= now ? sharedLast : 0;
  return Math.max(ownLast, shared);
}

export function idleSignedOutText(minutes) {
  return `Signed out after ${minutes} minutes without use on this device.`;
}
